/**
 * EventTimelineStrip — every matching event across the window, and the range control for them.
 *
 * ─── IT IS A CONTROL FIRST AND A CHART SECOND ───
 * `WhenAnswer` already draws this aggregate, and draws it differently on purpose: it trims the
 * axis to the first and last match, because as an ANSWER the shape of the busy part is the whole
 * point and fourteen empty hours would squeeze it into a sixth of the width.
 *
 * This one cannot do that. It is what the operator drags on, so its axis has to be the window
 * the query ran against — the whole of it, zero-filled — or a drag would land on a different
 * time than the one under the cursor. That is also why the window comes from the SERVER
 * (`result.window`): the client sends a preset, the node resolves it in its own local time, and
 * a cloud operator two timezones away would otherwise brush against a different day than the
 * one that was searched.
 *
 * ─── TWO TIERS, AND WHY THE TOP ONE ZOOMS ───
 * The PLOT draws the selected slice; the SCRUBBER underneath always draws the whole window with
 * that slice marked on it. That split is what makes a selection readable in both directions: the
 * plot gives the chosen hours enough width to have a shape, and the scrubber keeps them in the
 * context that says whether they were the busy part or the quiet part. It also settles which
 * control does what — the plot narrows, the scrubber widens and moves — so a selection is never
 * a trap you cannot get back out of, which a single self-zooming chart always is.
 *
 * Both tiers are slices of ONE fetch. Zooming costs no request, which is the only reason the
 * plot can afford to follow the selection at all.
 *
 * ─── THE BARS FOLLOW EVERY FILTER EXCEPT THIS ONE ───
 * The chart is drawn from the same filters as the answer — a colour, a camera, a model — so
 * picking "red top" visibly reshapes the day rather than leaving a chart that describes a query
 * nobody is running. The one filter it ignores is its own selection, because a chart redrawn to
 * the range it is selecting has nothing left to drag against.
 *
 * ─── ONE SCALE, ONE HUE, AND THE FIGURES WRITTEN OUT ───
 * A single series needs no legend and no palette: the title names it, the peak carries its own
 * label, the y-scale states its maximum and the axis states both ends. Severity is the only
 * other colour, it appears only on the marker rail, and it always ships with its word.
 *
 * NOTE on that word: the projection returns `severity: null` for these rows on a live node, so
 * a pin is coloured from its MODEL and only labelled from `severity` when the row carries one.
 */
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { SearchEventRow, SearchResult } from '../../api/contracts';
import { MODEL_LABEL, titleCase } from './searchModel';

interface TimelineRow {
  bucket: string;
  events: number;
  distinct_persons?: number;
}

export interface TimeRange { from: string; to: string }

export interface EventTimelineStripProps {
  /** The `timeline` aggregate over the window, with the range dropped. Null while unfetched. */
  result: SearchResult | null;
  loading?: boolean;
  /** The selection, or null for the whole window. */
  range: TimeRange | null;
  onRangeChange: (range: TimeRange | null) => void;
  /**
   * High-severity events inside the window, for the marker rail.
   *
   * A separate fetch because the timeline aggregate returns counts and nothing else — it cannot
   * say that two of an hour's ninety events were a fire. These are the rows themselves, capped,
   * which is affordable precisely because the events that earn a pin are rare. Empty or omitted
   * hides the rail rather than drawing an empty one.
   */
  markers?: SearchEventRow[];
  /** Names a camera for a marker's tooltip. Falls back to a short id. */
  cameraName?: (id: string) => string | undefined;
  /**
   * Whether the strip starts folded away. Default TRUE, and that is the interesting part.
   *
   * It is a control, not the answer: an operator arrives with a question, reads the answer, and
   * only then asks "where in the day was this". Open by default it spent 150px of the fold on a
   * chart nobody had asked for yet and pushed the first row of results off small screens. Folded,
   * the header still names it, still carries the window, the busiest bucket and the clear-range
   * button — so the narrowing it offers stays discoverable, and any range already selected is
   * still visible and still removable without opening anything.
   */
  defaultCollapsed?: boolean;
}

/** One bucket, and the wall-clock span it covers. */
interface Bucket { from: number; to: number; events: number }

const DAY_MS = 86_400_000;

/**
 * The buckets that had rows → a contiguous run across the WHOLE window.
 *
 * Missing buckets are zeros, because a chart that closes its own gaps turns a quiet afternoon
 * into a continuous run of activity. The run starts at the window's own edge rather than the
 * first match, because the axis has to be a map of the window for a drag on it to mean anything.
 */
function toBuckets(
  rows: TimelineRow[],
  bucket: 'hour' | 'day',
  windowFrom: number,
  windowTo: number,
): Bucket[] {
  const counts = new Map<number, number>();
  for (const r of rows) {
    const t = new Date(r.bucket).getTime();
    if (!Number.isFinite(t)) continue;
    counts.set(t, (counts.get(t) || 0) + (r.events || 0));
  }

  /*
   * EVERY BOUNDARY HERE IS A LOCAL ONE, and it has to be.
   *
   * The counts above are keyed by the server's `date_trunc('hour'|'day', event_timestamp)`,
   * which snaps to the start of the local hour or day in the DATABASE's timezone. This axis
   * has to land on exactly those instants or `counts.get(t)` misses every one of them and the
   * whole chart draws as empty — which is what `Math.floor(windowFrom / HOUR_MS) * HOUR_MS`
   * did here: it floors to a UTC hour, so on a node at +05:30 (or +05:45, or +09:30) the grid
   * sat 30 minutes off the server's and NOTHING ever matched. It looked correct in every
   * whole-hour timezone, which is exactly why it shipped.
   *
   * `setMinutes(0,0,0)` and `setHours(h+1)` go through the Date API rather than doing integer
   * arithmetic on the epoch, so the boundaries stay local across a DST change too instead of
   * every bucket after it drifting by an hour.
   */
  const first = bucket === 'day' ? floorDayLocal(windowFrom) : floorHourLocal(windowFrom);
  const next = bucket === 'day' ? addDay : addHour;

  const out: Bucket[] = [];
  for (let t = first; t < windowTo; t = next(t)) {
    const end = next(t);
    out.push({ from: t, to: Math.min(end, windowTo), events: counts.get(t) || 0 });
    // A pathological window (a bad custom range) must not spin here.
    if (out.length > 800) break;
  }
  return out;
}

function floorHourLocal(t: number): number {
  const d = new Date(t);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

function floorDayLocal(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function addHour(t: number): number {
  const d = new Date(t);
  d.setHours(d.getHours() + 1);
  return d.getTime();
}

function addDay(t: number): number {
  const d = new Date(t);
  d.setDate(d.getDate() + 1);
  return d.getTime();
}

const clock = (t: number) => new Date(t).toLocaleTimeString([], {
  hour: '2-digit', minute: '2-digit', hour12: false,
});
const dayLabel = (t: number) => new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' });

/**
 * Ruler ticks: four to nine of them, on a round number of buckets.
 *
 * Chosen by bucket COUNT rather than by clock interval, so the labels always sit exactly on a
 * bucket edge — the gridlines are drawn from the same numbers, and one landing mid-bar would
 * make the plot look misaligned with its own axis.
 */
function tickEvery(n: number): number {
  for (const step of [1, 2, 3, 4, 6, 8, 12, 24, 48]) {
    if (n / step <= 9) return step;
  }
  return Math.ceil(n / 9);
}

/** Position a label by percent, pulling the outermost two inside the box. */
function edgeSafe(pct: number): { left: string; transform: string } {
  return {
    left: `${pct}%`,
    transform: pct < 5 ? 'translateX(0)' : pct > 95 ? 'translateX(-100%)' : 'translateX(-50%)',
  };
}

/**
 * A pin's tone comes from its MODEL, not from the row's `severity`.
 *
 * `severity` is nullable on the projection and comes back null for every one of these rows on a
 * live node — so reading a colour off it would silently paint every pin with the fallback and
 * make the encoding a lie. The model is what actually decides: everything the rail can show is
 * a model the node treats as critical, which is the whole reason those four earn a pin.
 *
 * `severity` is still shown in the tooltip when the row carries one, because when it IS set it
 * is the node's own judgement and worth more than an inference from the model.
 */
const CRITICAL_TONE = 'var(--destructive)';

export default function EventTimelineStrip({
  result,
  loading = false,
  range,
  onRangeChange,
  markers = [],
  cameraName,
  defaultCollapsed = true,
}: EventTimelineStripProps) {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const ovRef = useRef<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  /** An in-progress drag on the plot, as indices into the VISIBLE slice. */
  const [drag, setDrag] = useState<{ anchor: number; head: number } | null>(null);
  /** An in-progress drag on the scrubber, as ABSOLUTE bucket indices. */
  const [ovDrag, setOvDrag] = useState<{ lo: number; hi: number } | null>(null);

  const bucket = (result?.bucket || 'hour') as 'hour' | 'day';
  const win = result?.window;
  const windowFrom = win ? new Date(win.from).getTime() : NaN;
  const windowTo = win ? new Date(win.to).getTime() : NaN;

  const buckets = useMemo(() => {
    if (!result || !Number.isFinite(windowFrom) || !Number.isFinite(windowTo)) return [];
    return toBuckets((result.rows || []) as TimelineRow[], bucket, windowFrom, windowTo);
  }, [result, bucket, windowFrom, windowTo]);

  const label = bucket === 'day' ? dayLabel : clock;
  const unitWord = bucket === 'day' ? 'day' : 'hour';

  /** The applied selection, as absolute bucket indices. */
  const applied = useMemo<[number, number] | null>(() => {
    if (!range || !buckets.length) return null;
    const from = new Date(range.from).getTime();
    const to = new Date(range.to).getTime();
    let lo = -1;
    let hi = -1;
    buckets.forEach((b, i) => {
      // Any overlap counts, so a range set while the server was on hour buckets still draws
      // sensibly once the window widens and it switches to days.
      if (b.to > from && b.from < to) {
        if (lo === -1) lo = i;
        hi = i;
      }
    });
    return lo === -1 ? null : [lo, hi];
  }, [range, buckets]);

  /** What the plot draws: the selection while there is one, else the whole window. */
  const view = useMemo<[number, number]>(() => {
    if (ovDrag) return [ovDrag.lo, ovDrag.hi];
    if (applied) return applied;
    return [0, Math.max(0, buckets.length - 1)];
  }, [ovDrag, applied, buckets.length]);

  const visible = useMemo(() => buckets.slice(view[0], view[1] + 1), [buckets, view]);

  const max = useMemo(() => visible.reduce((m, b) => Math.max(m, b.events), 0), [visible]);
  const viewEvents = useMemo(() => visible.reduce((n, b) => n + b.events, 0), [visible]);
  const allEvents = useMemo(() => buckets.reduce((n, b) => n + b.events, 0), [buckets]);
  const peakIndex = useMemo(
    () => (max > 0 ? visible.findIndex((b) => b.events === max) : -1),
    [visible, max],
  );
  const ovMax = useMemo(() => buckets.reduce((m, b) => Math.max(m, b.events), 0), [buckets]);

  /** The live plot selection, in visible-slice indices. */
  const sel = useMemo<[number, number] | null>(() => (
    drag ? [Math.min(drag.anchor, drag.head), Math.max(drag.anchor, drag.head)] : null
  ), [drag]);

  const commitAbsolute = useCallback((lo: number, hi: number) => {
    if (!buckets.length) return;
    const a = buckets[Math.max(0, Math.min(lo, buckets.length - 1))];
    const b = buckets[Math.max(0, Math.min(hi, buckets.length - 1))];
    if (!a || !b) return;
    // Selecting everything is not a filter, it is the absence of one — and leaving a chip that
    // says "the whole window" would read as a narrowing that is not happening.
    if (lo <= 0 && hi >= buckets.length - 1) { onRangeChange(null); return; }
    onRangeChange({ from: new Date(a.from).toISOString(), to: new Date(b.to).toISOString() });
  }, [buckets, onRangeChange]);

  // ── Plot drag: narrows within what is on screen ──
  const plotIndexAt = useCallback((clientX: number) => {
    const box = plotRef.current?.getBoundingClientRect();
    if (!box || !box.width || !visible.length) return 0;
    const pct = (clientX - box.left) / box.width;
    return Math.min(visible.length - 1, Math.max(0, Math.floor(pct * visible.length)));
  }, [visible.length]);

  const onPlotDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!visible.length) return;
    const i = plotIndexAt(e.clientX);
    setDrag({ anchor: i, head: i });
    plotRef.current?.setPointerCapture(e.pointerId);
    // preventDefault stops the drag selecting page text, and would also swallow the focus the
    // click should have given the plot — so focus is moved by hand, or the arrow keys would
    // only ever work for someone who tabbed here.
    plotRef.current?.focus();
    e.preventDefault();
  };

  const onPlotMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const i = plotIndexAt(e.clientX);
    if (i !== drag.head) setDrag({ ...drag, head: i });
  };

  const endPlotDrag = () => {
    if (!drag) return;
    const lo = Math.min(drag.anchor, drag.head);
    const hi = Math.max(drag.anchor, drag.head);
    setDrag(null);
    // A press without a drag selects that one bucket. Treating it as "selected nothing" would
    // make a click on a busy hour clear the range instead of zooming into it.
    commitAbsolute(view[0] + lo, view[0] + hi);
  };

  const onPlotKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!buckets.length) return;
    if (e.key === 'Escape' && range) { onRangeChange(null); e.preventDefault(); return; }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const step = e.key === 'ArrowLeft' ? -1 : 1;
    const cur = applied ?? [0, buckets.length - 1];
    if (e.shiftKey) {
      commitAbsolute(cur[0], Math.min(buckets.length - 1, Math.max(cur[0], cur[1] + step)));
    } else {
      const lo = Math.min(buckets.length - 1, Math.max(0, cur[0] + step));
      commitAbsolute(lo, lo + (cur[1] - cur[0]));
    }
    e.preventDefault();
  };

  // ── Scrubber drag: widens, moves, or jumps ──
  const ovIndexAt = useCallback((clientX: number) => {
    const box = ovRef.current?.getBoundingClientRect();
    if (!box || !box.width || !buckets.length) return 0;
    const pct = (clientX - box.left) / box.width;
    return Math.min(buckets.length - 1, Math.max(0, Math.floor(pct * buckets.length)));
  }, [buckets.length]);

  const ovMode = useRef<'l' | 'r' | 'mid' | 'new' | null>(null);
  const ovAnchor = useRef(0);
  const ovGrab = useRef(0);

  const onOvDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!buckets.length) return;
    const grab = (e.target as HTMLElement)?.getAttribute?.('data-grab') as
      'l' | 'r' | 'mid' | null;
    const i = ovIndexAt(e.clientX);
    const cur = applied ?? [0, buckets.length - 1];
    if (grab === 'l' || grab === 'r') {
      ovMode.current = grab;
      setOvDrag({ lo: cur[0], hi: cur[1] });
    } else if (grab === 'mid') {
      ovMode.current = 'mid';
      ovGrab.current = i - cur[0];
      setOvDrag({ lo: cur[0], hi: cur[1] });
    } else {
      ovMode.current = 'new';
      ovAnchor.current = i;
      setOvDrag({ lo: i, hi: i });
    }
    ovRef.current?.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onOvMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!ovMode.current || !ovDrag) return;
    const i = ovIndexAt(e.clientX);
    const last = buckets.length - 1;
    let { lo, hi } = ovDrag;
    if (ovMode.current === 'new') {
      lo = Math.min(ovAnchor.current, i);
      hi = Math.max(ovAnchor.current, i);
    } else if (ovMode.current === 'l') {
      lo = Math.min(i, hi);
    } else if (ovMode.current === 'r') {
      hi = Math.max(i, lo);
    } else {
      const width = hi - lo;
      lo = Math.max(0, Math.min(i - ovGrab.current, last - width));
      hi = lo + width;
    }
    if (lo !== ovDrag.lo || hi !== ovDrag.hi) setOvDrag({ lo, hi });
  };

  const endOvDrag = () => {
    if (!ovMode.current || !ovDrag) { ovMode.current = null; setOvDrag(null); return; }
    const wasNew = ovMode.current === 'new';
    const { lo, hi } = ovDrag;
    ovMode.current = null;
    setOvDrag(null);
    // A press without a drag on the scrubber reads as "take me here", not "select nothing".
    if (wasNew && lo === hi) {
      const span = applied ? applied[1] - applied[0] : 0;
      commitAbsolute(lo, Math.min(buckets.length - 1, lo + span));
      return;
    }
    commitAbsolute(lo, hi);
  };

  // ── Marker rail ──
  /** Pins grouped onto the visible buckets, so two in one hour are one pin that says ×2. */
  const pins = useMemo(() => {
    if (!visible.length || !markers.length) return [];
    const lo = visible[0].from;
    const hi = visible[visible.length - 1].to;
    const byCol = new Map<number, SearchEventRow[]>();
    for (const m of markers) {
      const at = new Date(m.event_timestamp).getTime();
      if (!Number.isFinite(at) || at < lo || at >= hi) continue;
      const idx = visible.findIndex((b) => at >= b.from && at < b.to);
      if (idx < 0) continue;
      const list = byCol.get(idx) || [];
      list.push(m);
      byCol.set(idx, list);
    }
    return [...byCol.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([idx, rows], order) => ({ idx, rows, order }));
  }, [visible, markers]);

  const ticks = useMemo(() => {
    if (!visible.length) return [];
    const every = tickEvery(visible.length);
    const out: Array<{ i: number; t: number }> = [];
    for (let i = 0; i < visible.length; i += every) out.push({ i, t: visible[i].from });
    // The closing edge, so the axis names both ends of what is drawn.
    out.push({ i: visible.length, t: visible[visible.length - 1].to });
    return out;
  }, [visible]);

  // ── The states that are not a chart ──
  if (!result && loading) {
    return (
      <Shell collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)}>
        <div className="h-[132px] animate-pulse rounded-md bg-muted" />
      </Shell>
    );
  }
  if (!buckets.length) {
    return (
      <Shell collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)}>
        <p className="py-6 text-center text-xs text-muted-foreground">
          {result
            ? 'No events in this window to plot. Widen the time window, or remove a filter.'
            : 'The timeline could not be loaded. The rest of the search is unaffected.'}
        </p>
      </Shell>
    );
  }

  const first = visible[0];
  const last = visible[visible.length - 1];
  const zoomed = view[0] > 0 || view[1] < buckets.length - 1;

  return (
    <Shell
      collapsed={collapsed}
      onToggle={() => setCollapsed((c) => !c)}
      sub={`${bucket === 'day' ? 'daily' : 'hourly'} buckets · ${label(first.from)}–${label(last.to)}`}
      peak={max > 0
        ? `busiest ${unitWord} ${max} at ${label(visible[peakIndex].from)}`
        : 'nothing recorded here'}
      action={range && (
        <button
          type="button"
          onClick={() => onRangeChange(null)}
          className="inline-flex items-center gap-1 rounded-md border border-brand bg-brand-light px-2 py-1 text-[11px] text-foreground hover:bg-brand-lighter"
          title="Search the whole window again"
        >
          <XMarkIcon className="h-3 w-3" />
          {label(new Date(range.from).getTime())} – {label(new Date(range.to).getTime())}
        </button>
      )}
    >
      {/* ── Marker rail. The events too rare to survive a bar chart get a pin instead: two
             fires inside nine hundred person detections would round to nothing. Severity is
             the only colour here and it always ships with its word. ── */}
      {pins.length > 0 && (
        <div className="relative h-[26px]">
          {pins.map(({ idx, rows, order }) => {
            const pct = ((idx + 0.5) / visible.length) * 100;
            const model = MODEL_LABEL[rows[0].model_key] || titleCase(rows[0].model_key || '');
            const cam = cameraName?.(rows[0].camera_id) || rows[0].camera_id?.slice(0, 8);
            const sev = rows[0].severity ? titleCase(rows[0].severity) : 'Critical';
            return (
              <button
                key={`${idx}-${rows[0].event_id}`}
                type="button"
                onClick={() => commitAbsolute(view[0] + idx, view[0] + idx)}
                style={edgeSafe(pct)}
                className="absolute bottom-0 flex flex-col items-center"
                title={`${sev} · ${model}${rows.length > 1 ? ` ×${rows.length}` : ''} · ${clock(new Date(rows[0].event_timestamp).getTime())} · ${cam}`}
              >
                <span
                  className="whitespace-nowrap rounded px-1 text-[9px] font-semibold uppercase tracking-wide text-white"
                  style={{ background: CRITICAL_TONE }}
                >
                  {model}
                  {rows.length > 1 ? ` ×${rows.length}` : ''}
                </span>
                {/* Neighbouring pins stagger between two stem lengths so their labels stack
                    instead of overprinting; the stem still lands on the real bucket. */}
                <span
                  className="w-px"
                  style={{ background: CRITICAL_TONE, height: order % 2 ? '13px' : '5px' }}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* ── Ruler ── */}
      <div className="relative h-[15px] border-b border-border">
        {ticks.map(({ i, t }) => {
          const pct = (i / visible.length) * 100;
          return (
            <span key={`r${i}`} className="contents">
              <span
                style={edgeSafe(pct)}
                className="absolute top-0 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground"
              >
                {label(t)}
              </span>
              <i
                style={{ left: `${pct}%` }}
                className="absolute bottom-0 h-[5px] w-px bg-border"
              />
            </span>
          );
        })}
      </div>

      {/* ── Plot. Gap-free: each bucket owns exactly 1/n of the width, so the selection
             overlay, the bars, the gridlines and the axis all resolve against one scale. The
             breathing room between bars is padding INSIDE each column — a flex `gap` here
             would drift the overlay off the bars it is meant to be selecting. ── */}
      <div
        ref={plotRef}
        role="group"
        tabIndex={0}
        aria-label={
          `Event timeline, ${visible.length} ${unitWord}${visible.length === 1 ? '' : 's'} shown. `
          + 'Drag across the bars to narrow, use the strip below to widen. '
          + (range ? `Selected ${label(first.from)} to ${label(last.to)}, ${viewEvents} events. Escape clears it.`
            : `Whole window, ${allEvents} events.`)
        }
        onPointerDown={onPlotDown}
        onPointerMove={onPlotMove}
        onPointerUp={endPlotDrag}
        onPointerCancel={endPlotDrag}
        onKeyDown={onPlotKey}
        className="relative flex h-[104px] w-full cursor-crosshair touch-none select-none items-end border-b border-border pt-3 outline-none focus-visible:ring-2 focus-visible:ring-brand-ring"
      >
        {/* The scale's own box starts below the padding the peak label lives in, so the "max"
            rule sits where the tallest bar actually reaches. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-3">
          {ticks.map(({ i }) => (
            <i
              key={`g${i}`}
              style={{ left: `${(i / visible.length) * 100}%` }}
              className="absolute inset-y-0 w-px bg-border opacity-70"
            />
          ))}
          <b className="absolute inset-x-0 top-0 block h-px bg-border opacity-70" />
          <b className="absolute inset-x-0 top-1/2 block h-px bg-border opacity-70" />
          {max > 0 && (
            <span className="absolute right-0.5 top-0 -translate-y-full text-[9.5px] tabular-nums text-muted-foreground">
              {max} max
            </span>
          )}
        </div>

        {sel && (
          <div
            className="pointer-events-none absolute inset-y-0 border-x-2 border-brand bg-brand-light"
            style={{
              left: `${(sel[0] / visible.length) * 100}%`,
              width: `${((sel[1] - sel[0] + 1) / visible.length) * 100}%`,
            }}
          />
        )}

        {visible.map((b, i) => {
          const outside = !!sel && (i < sel[0] || i > sel[1]);
          const isPeak = i === peakIndex && b.events > 0;
          return (
            <div
              key={b.from}
              className="relative flex h-full min-w-0 flex-1 items-end px-[2px]"
              title={`${bucket === 'day' ? dayLabel(b.from) : `${clock(b.from)}–${clock(b.to)}`} · ${b.events} ${b.events === 1 ? 'event' : 'events'}`}
            >
              {b.events > 0 ? (
                <div
                  className="w-full rounded-t-[3px]"
                  style={{
                    height: `${Math.max(3, Math.round((b.events / Math.max(1, max)) * 100))}%`,
                    // One hue for one series: the peak is the full brand colour, the rest a
                    // tint of it, so the busiest bucket reads without a second colour.
                    background: isPeak
                      ? 'var(--brand)'
                      : 'color-mix(in srgb, var(--brand) 55%, transparent)',
                    opacity: outside ? 0.3 : 1,
                  }}
                />
              ) : (
                // An empty bucket is drawn, never closed up: a quiet hour is an answer.
                <div className="h-px w-full bg-border" />
              )}
              {isPeak && (
                <span
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[9.5px] tabular-nums text-foreground"
                  style={{ bottom: `${Math.max(3, Math.round((b.events / Math.max(1, max)) * 100))}%` }}
                >
                  {b.events}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Axis ── */}
      <div className="relative mt-1 h-[13px]">
        {ticks.map(({ i, t }) => (
          <span
            key={`a${i}`}
            style={edgeSafe((i / visible.length) * 100)}
            className="absolute top-0 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground"
          >
            {label(t)}
          </span>
        ))}
      </div>

      {/* ── Whole-window scrubber. Always the full window, with the plot's slice marked on it:
             drag an edge to widen, drag the middle to move, drag the track to reselect. ── */}
      <div className="mt-3">
        <div className="mb-1 flex items-baseline gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Whole window
          </span>
          <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
            {label(buckets[0].from)} – {label(buckets[buckets.length - 1].to)}
            {zoomed && <> · showing <strong className="text-foreground">{viewEvents}</strong> of {allEvents}</>}
          </span>
        </div>
        <div
          ref={ovRef}
          onPointerDown={onOvDown}
          onPointerMove={onOvMove}
          onPointerUp={endOvDrag}
          onPointerCancel={endOvDrag}
          className="relative h-[30px] cursor-crosshair touch-none select-none overflow-hidden rounded-md border border-border bg-muted"
        >
          <div className="absolute inset-x-0 bottom-0 top-1 flex items-end gap-px">
            {buckets.map((b) => (
              <i
                key={`o${b.from}`}
                className="flex-1 rounded-t-sm"
                style={{
                  height: `${Math.max(1, Math.round((b.events / Math.max(1, ovMax)) * 100))}%`,
                  background: b.to > first.from && b.from < last.to
                    ? 'color-mix(in srgb, var(--brand) 62%, transparent)'
                    : 'var(--border)',
                }}
              />
            ))}
          </div>
          <div
            className="absolute inset-y-0 border-x-2 border-brand bg-brand-light"
            style={{
              left: `${(view[0] / buckets.length) * 100}%`,
              width: `${((view[1] - view[0] + 1) / buckets.length) * 100}%`,
              // With no range set the window IS the whole strip, so it must not swallow the
              // drag that would create one.
              pointerEvents: zoomed ? undefined : 'none',
            }}
          >
            {zoomed && (
              <>
                <div data-grab="mid" className="absolute inset-0 cursor-grab active:cursor-grabbing" />
                <div data-grab="l" className="absolute inset-y-0 left-0 w-2.5 cursor-ew-resize" />
                <div data-grab="r" className="absolute inset-y-0 right-0 w-2.5 cursor-ew-resize" />
              </>
            )}
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-muted-foreground">
        {zoomed
          ? 'Drag the bars above to narrow further; drag an edge of the strip to widen, or its middle to move. Click a bar to zoom into that '
          : 'Drag across the bars to search one slice of the window — everything above follows it. Click a bar for a single '}
        {unitWord}.
      </p>
    </Shell>
  );
}

/** The dock: one bordered strip, titled and collapsible, so it reads as a control. */
function Shell({
  children,
  sub,
  peak,
  action,
  collapsed,
  onToggle,
}: {
  children: ReactNode;
  sub?: string;
  peak?: string;
  action?: ReactNode;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="shrink-0 border-t border-border bg-card px-4 pb-2 pt-2">
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
          title={collapsed ? 'Show the timeline' : 'Hide the timeline'}
        >
          {collapsed ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
          Event timeline
        </button>
        {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
        {peak && <span className="text-[11px] tabular-nums text-muted-foreground">· {peak}</span>}
        <div className="ml-auto">{action}</div>
      </div>
      {!collapsed && <div className="mt-1.5">{children}</div>}
    </div>
  );
}
